import { useRef, useState } from "react";

import { api, errorMessage } from "../api/client";
import { formatCount, formatDateTime, shortDay } from "../lib/format";
import { ui } from "../lib/ui";
import { useAsync } from "../lib/useAsync";
import type { DateRange, SalesStatus, SalesUpload } from "../types";

export interface SalesBranch {
  id: string;
  name: string;
  color: string;
}

interface SalesPanelProps {
  branches: SalesBranch[];
  range: DateRange;
  /** Sales on file changed — the dashboard re-reads its series. */
  onChanged: () => void;
}

/** One row per store on the dashboard: what sales it has on file, the
 *  upload that attaches the POS export (an `.xlsx` or `.csv` with a row per
 *  day and billing type), and removal of the range shown. The file goes to
 *  the API as-is; only the daily sums are kept. */
export function SalesPanel({ branches, range, onChanged }: SalesPanelProps) {
  const ids = branches.map((b) => b.id).join(",");
  const statuses = useAsync(
    () => Promise.all(branches.map((b) => api.salesStatus(b.id))),
    [ids],
  );
  const byId = new Map((statuses.data ?? []).map((s) => [s.branch_id, s]));
  const changed = () => {
    statuses.reload();
    onChanged();
  };
  if (branches.length === 0) {
    return (
      <p className={`${ui.notice} mb-0 text-muted`}>
        Add a branch row above to attach its sales export and see its conversion rate.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {branches.map((branch) => (
        <SalesRow
          key={branch.id}
          branch={branch}
          status={byId.get(branch.id) ?? null}
          loading={statuses.loading}
          range={range}
          onChanged={changed}
        />
      ))}
    </ul>
  );
}

function SalesRow({
  branch,
  status,
  loading,
  range,
  onChanged,
}: {
  branch: SalesBranch;
  status: SalesStatus | null;
  loading: boolean;
  range: DateRange;
  onChanged: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SalesUpload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.uploadSales(branch.id, file));
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        `Remove ${branch.name}'s sales from ${range.from} to ${range.to}? Footfall counts are not affected.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      await api.deleteSales(branch.id, range);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onFile = status && status.days > 0;
  return (
    <li className="flex flex-col gap-2 rounded-lg border border-border bg-bg px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-3">
        <span
          aria-hidden="true"
          className="inline-block size-3 rounded-full ring-2 ring-surface"
          style={{ background: branch.color }}
        />
        <span className="font-medium">{branch.name}</span>
        <span className="text-xs text-muted">
          {loading && !status
            ? "Checking…"
            : onFile
              ? `${formatCount(status.days)} days on file · ${shortDay(status.first_day!)} – ${shortDay(status.last_day!)} · uploaded ${formatDateTime(status.uploaded_at)}`
              : "No sales on file — attach the POS export to see conversion."}
        </span>
        <span className="ml-auto flex items-center gap-2">
          <input
            ref={input}
            type="file"
            accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <button
            type="button"
            className={`${ui.btnQuiet} py-1.5! text-[13px]`}
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? "Working…" : onFile ? "Upload more…" : "Attach sales export…"}
          </button>
          {onFile && (
            <button
              type="button"
              className={`${ui.btnDanger} py-1.5! text-[13px]`}
              disabled={busy}
              title={`Remove this store's sales between ${range.from} and ${range.to}`}
              onClick={() => void remove()}
            >
              Remove in range
            </button>
          )}
        </span>
      </div>
      {error && <p className={`${ui.notice} ${ui.noticeError} mb-0`}>{error}</p>}
      {result && (
        <div className={`${ui.notice} ${ui.noticeSuccess} mb-0 flex flex-col gap-1`}>
          <span>
            Loaded {formatCount(result.rows)} rows over {formatCount(result.days)} days
            ({shortDay(result.first_day)} – {shortDay(result.last_day)})
            {result.branch_codes.length > 0 ? ` · POS branch ${result.branch_codes.join(", ")}` : ""}.
            Those days were replaced; other days on file were kept.
          </span>
          <span className="text-xs">
            Billing types → channel:{" "}
            {Object.entries(result.channels)
              .map(([type, channel]) => `${type} = ${channel}`)
              .join(" · ")}
            . Only <b>store</b> transactions count as conversions.
          </span>
        </div>
      )}
    </li>
  );
}
